// Per-insurance-type fields for quotes — the single source of truth shared by
// the Quote editor (QuotesInvoicesView) and the public/detail views
// (QuoteLandingPage, QuoteDetail). The backend stores these as free-form JSON
// (QuoteItem.coverageDetails), so adding a type or field here is frontend-only.
//
// Mirrors the insurance types Deskte already uses for policies (PolicyType enum)
// and the field vocabulary from the policy form (vehicle/dwelling/face amount…),
// so a quote and the policy it becomes line up.

import { tiposPropiosDeLaAgencia } from '../policyTypes.js';

// Plan types — every PolicyType the backend supports, with bilingual labels.
// `v` must match the Prisma PolicyType enum (it is sent as QuoteItem.planType
// and mapped back to a PolicyType when a quote converts to a policy).
export const PLAN_TYPES = [
  { v: 'HEALTH', es: 'Salud', en: 'Health' },
  { v: 'DENTAL', es: 'Dental', en: 'Dental' },
  { v: 'VISION', es: 'Visión', en: 'Vision' },
  { v: 'MEDICARE_ADVANTAGE', es: 'Medicare Advantage', en: 'Medicare Advantage' },
  { v: 'MEDICARE_SUPPLEMENT', es: 'Medicare Supplement', en: 'Medicare Supplement' },
  { v: 'LIFE', es: 'Vida', en: 'Life' },
  { v: 'ANNUITY', es: 'Anualidad', en: 'Annuity' },
  { v: 'AUTO', es: 'Auto', en: 'Auto' },
  { v: 'HOME', es: 'Hogar', en: 'Home' },
  { v: 'RENTERS', es: 'Inquilinos', en: 'Renters' },
  { v: 'FLOOD', es: 'Inundación', en: 'Flood' },
  { v: 'UMBRELLA', es: 'Paraguas (Umbrella)', en: 'Umbrella' },
  { v: 'DISABILITY', es: 'Discapacidad', en: 'Disability' },
  { v: 'LONG_TERM_CARE', es: 'Cuidado a largo plazo', en: 'Long-term care' },
  { v: 'COMMERCIAL', es: 'Comercial', en: 'Commercial' },
  { v: 'WORKERS_COMP', es: 'Compensación laboral', en: 'Workers comp' },
  { v: 'PET', es: 'Mascotas', en: 'Pet' },
  { v: 'TRAVEL', es: 'Viaje', en: 'Travel' },
];

/**
 * LOS TIPOS QUE SE PUEDEN ELEGIR EN UNA COTIZACION: los del enum y, detrás, los
 * que se creó la agencia en "Gestionar tipos de póliza".
 *
 * Wendy Cercio (Insurance Latino), 2026-10-18: *"Puedes incluir en Cotizaciones
 * -> Tipo -> SEGURO PRIVADO, como lo tengo en las Pólizas"*. Un tipo propio se
 * guarda con su id (`custom_…`), igual que en Pólizas, y al convertir la
 * cotización el servidor lo sella en la póliza (`tipoDeLaOpcionDeCotizacion`).
 *
 * Se calcula al pedirla: los tipos propios se cargan al arrancar la sesión, y
 * una lista congelada al importar el archivo saldría sin ellos.
 */
export function tiposDeCotizacion() {
  return [
    ...PLAN_TYPES,
    ...tiposPropiosDeLaAgencia().map((p) => ({
      v: p.id, es: p.labelEs || p.label || p.id, en: p.label || p.labelEs || p.id, propio: true,
    })),
  ];
}

/** El nombre de un tipo de cotización en el idioma de la pantalla. */
export function nombreDelTipoDeCotizacion(v, es) {
  const t = tiposDeCotizacion().find((x) => x.v === v);
  if (t) return es ? t.es : t.en;
  return String(v || '').replace(/_/g, ' ');
}

// Field catalog. `key` is what we store in coverageDetails; labels bilingual;
// `ph` is the input placeholder (an example, not a default value).
const FIELDS = {
  deductible: { key: 'deductible', es: 'Deducible', en: 'Deductible', ph: '$1,500' },
  // Split deductible — some marketplace plans track medical and drugs
  // separately (HealthCare.gov shows "Salud: $0 · Medicamentos: $500").
  deductibleHealth: { key: 'deductibleHealth', es: 'Deducible salud', en: 'Health deductible', ph: '$0' },
  deductibleDrugs: { key: 'deductibleDrugs', es: 'Deducible medicamentos', en: 'Drug deductible', ph: '$500' },
  // Totales familiares del Mercado (hogar de 2+ personas). `deductible` y
  // `oopMax` siguen siendo los individuales.
  deductibleFamily: { key: 'deductibleFamily', es: 'Deducible familiar', en: 'Family deductible', ph: '$3,000' },
  oopMaxFamily: { key: 'oopMaxFamily', es: 'Máximo de bolsillo familiar', en: 'Family out-of-pocket max', ph: '$16,000' },
  copay: { key: 'copay', es: 'Copago', en: 'Copay', ph: '$25' },
  copayPrimary: { key: 'copayPrimary', es: 'Copago primario', en: 'Primary copay', ph: '$25' },
  copaySpecialist: { key: 'copaySpecialist', es: 'Copago especialista', en: 'Specialist copay', ph: '$50' },
  copayUrgent: { key: 'copayUrgent', es: 'Copago urgencias', en: 'Urgent care copay', ph: '$65' },
  copayER: { key: 'copayER', es: 'Copago emergencia', en: 'ER copay', ph: '$300' },
  copayMentalHealth: { key: 'copayMentalHealth', es: 'Copago salud mental', en: 'Mental health copay', ph: '$60' },
  copayDrugGeneric: { key: 'copayDrugGeneric', es: 'Copago medicinas (genérico)', en: 'Drug copay (generic)', ph: '$10' },
  // Diagnostic rows from the marketplace comparison (HealthCare.gov / SBC).
  diagnosticTest: { key: 'diagnosticTest', es: 'Prueba diagnóstica (rayos X, laboratorio)', en: 'Diagnostic test (x-ray, blood work)', ph: '$30 copay' },
  imaging: { key: 'imaging', es: 'Imágenes (CT/PET, MRI)', en: 'Imaging (CT/PET, MRI)', ph: '$90 copay' },
  // Health/ACA money fields — the marketplace quoter fills these; on convert
  // they carry onto the policy (typeDetails.subsidy / typeDetails.planTotalCost).
  subsidy: { key: 'subsidy', es: 'Subsidio mensual', en: 'Monthly subsidy', ph: '$368' },
  planTotalCost: { key: 'planTotalCost', es: 'Costo total del plan', en: 'Plan total cost', ph: '$501' },
  benefitsLink: { key: 'benefitsLink', es: 'Resumen de beneficios (SBC)', en: 'Benefits summary (SBC)', ph: 'https://…' },
  oopMax: { key: 'oopMax', es: 'Máximo de bolsillo', en: 'Out-of-pocket max', ph: '$8,000' },
  network: { key: 'network', es: 'Red', en: 'Network', ph: 'PPO / HMO' },
  drugCoverage: { key: 'drugCoverage', es: 'Cobertura de medicinas', en: 'Drug coverage', ph: 'Incluida / $X' },
  // Auto
  liabilityLimits: { key: 'liabilityLimits', es: 'Límites de responsabilidad', en: 'Liability limits', ph: '100/300/100' },
  collisionDeductible: { key: 'collisionDeductible', es: 'Deducible colisión', en: 'Collision deductible', ph: '$500' },
  comprehensiveDeductible: { key: 'comprehensiveDeductible', es: 'Deducible comprensivo', en: 'Comprehensive deductible', ph: '$500' },
  vehicle: { key: 'vehicle', es: 'Vehículo', en: 'Vehicle', ph: '2022 Toyota Camry' },
  // Home / Renters / Flood
  dwellingCoverage: { key: 'dwellingCoverage', es: 'Cobertura de vivienda', en: 'Dwelling coverage', ph: '$300,000' },
  personalProperty: { key: 'personalProperty', es: 'Bienes personales', en: 'Personal property', ph: '$50,000' },
  liabilityCoverage: { key: 'liabilityCoverage', es: 'Responsabilidad', en: 'Liability', ph: '$300,000' },
  // Life / Annuity
  coverageAmount: { key: 'coverageAmount', es: 'Monto de cobertura', en: 'Coverage amount', ph: '$250,000' },
  productType: { key: 'productType', es: 'Tipo de producto', en: 'Product type', ph: 'Term / Whole / Universal' },
  termLength: { key: 'termLength', es: 'Plazo', en: 'Term length', ph: '20 años / 20 yrs' },
  cashValue: { key: 'cashValue', es: 'Valor en efectivo', en: 'Cash value', ph: '$0' },
  // Beneficios en vida (living benefits / cláusulas aceleradas) — Lissette
  // 2026-08-20: "en el quote de vida agregar para rellenar LOS CUBRIMIENTOS DE
  // BENEFICIOS EN VIDA (CRITICO, CRONICO Y TERMINAL) DETALLADOS".
  //
  // Son lo que de verdad decide una venta de Vida hoy, y hasta ahora no había
  // dónde escribirlos: el cliente comparaba dos cotizaciones por la prima y no
  // veía que una adelanta dinero si te da un infarto y la otra no.
  //
  // Van como texto libre a propósito: cada compañía los describe a su manera
  // ("hasta el 90% del beneficio", "máx. $500,000", "sujeto a tabla"), y
  // encerrarlos en un número obligaría a mentir para que quepan.
  // El enlace de la ilustracion de WinFlex. WinFlex no tiene una puerta publica
  // para conectarse (es de Zinnia, y sus integraciones van por acuerdo comercial
  // con plataformas socias), asi que lo que se guarda es la referencia: el
  // agente saca la ilustracion alli y pega aqui el enlace o el numero de caso.
  // El PDF se lee aparte con "Rellenar desde ilustracion"; esto es el rastro de
  // DE DONDE salieron esos numeros, para cuando alguien pregunte meses despues.
  //
  // `internal` lo deja FUERA de la pagina que ve el cliente: es la referencia de
  // trabajo del agente, y ademas el enlace pide sesion de WinFlex, asi que al
  // cliente solo le seria un enlace que no abre.
  illustrationLink: {
    key: 'illustrationLink', internal: true,
    es: 'Ilustración (WinFlex): enlace o # de caso',
    en: 'Illustration (WinFlex): link or case #',
    ph: 'https://winflexweb.com/... o caso 481902',
  },
  livingBenefits: { key: 'livingBenefits', es: '¿Incluye beneficios en vida?', en: 'Living benefits included?', ph: 'Sí, sin costo extra / Con jinete' },
  lbCritical: { key: 'lbCritical', es: 'Crítico — qué cubre y cuánto', en: 'Critical illness — what and how much', ph: 'Infarto, derrame, cáncer — hasta 90% del beneficio' },
  lbChronic: { key: 'lbChronic', es: 'Crónico — qué cubre y cuánto', en: 'Chronic illness — what and how much', ph: '2 de 6 actividades diarias — hasta $X/año' },
  lbTerminal: { key: 'lbTerminal', es: 'Terminal — qué cubre y cuánto', en: 'Terminal illness — what and how much', ph: 'Expectativa ≤ 12 meses — hasta 100%' },
  // Disability / Long-term care
  benefitAmount: { key: 'benefitAmount', es: 'Beneficio mensual', en: 'Monthly benefit', ph: '$2,000/mo' },
  eliminationPeriod: { key: 'eliminationPeriod', es: 'Período de espera', en: 'Elimination period', ph: '90 días' },
  benefitPeriod: { key: 'benefitPeriod', es: 'Período de beneficio', en: 'Benefit period', ph: '2 años / Vida' },
  // Commercial / Umbrella / Workers comp
  coverageLimit: { key: 'coverageLimit', es: 'Límite de cobertura', en: 'Coverage limit', ph: '$1,000,000' },
  coverageType: { key: 'coverageType', es: 'Tipo de cobertura', en: 'Coverage type', ph: 'BOP / General Liability' },
  // Pet
  annualLimit: { key: 'annualLimit', es: 'Límite anual', en: 'Annual limit', ph: '$5,000' },
  reimbursement: { key: 'reimbursement', es: 'Reembolso', en: 'Reimbursement', ph: '80%' },
  // Travel
  tripLength: { key: 'tripLength', es: 'Duración del viaje', en: 'Trip length', ph: '14 días' },
  // Seguro médico mayor fuera del ACA (TriTerm, Short Term): lo que PAGA el
  // cliente. Ver catalogoPlanesUhc.js.
  coinsurance: { key: 'coinsurance', es: 'Coseguro', en: 'Coinsurance', ph: '30%' },
  hospitalCare: { key: 'hospitalCare', es: 'Hospitalización', en: 'Hospital stay', ph: '30% tras deducible' },
  copayDrugBrand: { key: 'copayDrugBrand', es: 'Copago medicinas (marca)', en: 'Drug copay (brand)', ph: '$55 / $75 / 50%' },
  preventiveCare: { key: 'preventiveCare', es: 'Preventivo', en: 'Preventive care', ph: '$0, máx. $200' },
  maxBenefit: { key: 'maxBenefit', es: 'Beneficio máximo', en: 'Maximum benefit', ph: '$2 millones' },
  // Pagos fijos (HPG, Guard): lo que el plan LE PAGA al cliente. Todas las
  // etiquetas dicen "Le paga" — con las de arriba, "$5,000" de hospital se
  // leería como lo que paga el cliente, que es justo al revés.
  payOfficeVisit: { key: 'payOfficeVisit', es: 'Le paga por consulta (primario / especialista / urgente)', en: 'Pays you per visit (primary / specialist / urgent)', ph: '$80 / $100 / $100' },
  visitsPerYear: { key: 'visitsPerYear', es: 'Consultas pagadas al año', en: 'Paid visits per year', ph: '4 el año 1, 6 el año 2' },
  telehealth: { key: 'telehealth', es: 'Telemedicina', en: 'Telehealth', ph: '$0, ilimitada' },
  payER: { key: 'payER', es: 'Le paga por emergencia', en: 'Pays you per ER visit', ph: '$300 por día' },
  payHospitalDay: { key: 'payHospitalDay', es: 'Le paga por día de hospital', en: 'Pays you per hospital day', ph: '$2,000 por día' },
  payAdmission: { key: 'payAdmission', es: 'Le paga al ingresar al hospital', en: 'Pays you on hospital admission', ph: '$2,000, una vez' },
  payICU: { key: 'payICU', es: 'Le paga por día de UCI', en: 'Pays you per ICU day', ph: '$2,000 por día' },
  payLabXray: { key: 'payLabXray', es: 'Le paga por laboratorio / rayos X', en: 'Pays you for lab / x-ray', ph: '$30 / $50' },
  payImaging: { key: 'payImaging', es: 'Le paga por MRI, CT o PET', en: 'Pays you for MRI, CT or PET', ph: '$400' },
  payDrugs: { key: 'payDrugs', es: 'Le paga por receta (genérico / marca)', en: 'Pays you per prescription (generic / brand)', ph: '$10 / $40' },
  paySurgeryMax: { key: 'paySurgeryMax', es: 'Le paga por cirugía (máximo)', en: 'Pays you for surgery (max)', ph: '$25,000' },
  payWellness: { key: 'payWellness', es: 'Le paga por chequeo anual', en: 'Pays you for annual checkup', ph: '$100' },
  // Comunes a los planes del catálogo: lo que la clase manda decir SIEMPRE.
  // `wide`: son frases, no cifras; el editor les da la fila entera para que
  // se lean completas antes de enviar.
  waitingPeriods: { key: 'waitingPeriods', wide: true, es: 'Esperas', en: 'Waiting periods', ph: 'Preexistencias: 12 meses' },
  planWarnings: { key: 'planWarnings', wide: true, es: 'Importante', en: 'Important', ph: 'No es un plan ACA' },
  extraCosts: { key: 'extraCosts', wide: true, es: 'Costos adicionales', en: 'Additional costs', ph: 'Cuota de solicitud, membresía…' },
};

// Los campos de las dos familias del catálogo de UHC (catalogoPlanesUhc.js).
// Van por `coverageDetails.benefitStyle`, NO por el tipo de plan: los cuatro
// productos son HEALTH en la base, y con la lista de HEALTH se enseñarían
// subsidio y SBC, que no tienen, y se esconderían los "Le paga" que sí.
const BY_STYLE = {
  majorMedical: ['deductible', 'coinsurance', 'oopMax', 'copayPrimary', 'copaySpecialist', 'copayUrgent', 'copayER',
    'hospitalCare', 'copayDrugGeneric', 'copayDrugBrand', 'preventiveCare', 'maxBenefit', 'network',
    'waitingPeriods', 'planWarnings', 'extraCosts'],
  indemnity: ['payOfficeVisit', 'visitsPerYear', 'telehealth', 'payER', 'payHospitalDay', 'payAdmission', 'payICU',
    'payLabXray', 'payImaging', 'payDrugs', 'paySurgeryMax', 'payWellness', 'network',
    'waitingPeriods', 'planWarnings', 'extraCosts'],
};
export const BENEFIT_STYLES = Object.keys(BY_STYLE);

// Which fields show for each insurance type.
const BY_TYPE = {
  HEALTH: ['subsidy', 'planTotalCost', 'deductible', 'deductibleHealth', 'deductibleDrugs', 'deductibleFamily', 'copayPrimary', 'copaySpecialist', 'copayUrgent', 'copayER', 'copayMentalHealth', 'copayDrugGeneric', 'diagnosticTest', 'imaging', 'oopMax', 'oopMaxFamily', 'network', 'benefitsLink'],
  DENTAL: ['deductible', 'copay', 'oopMax', 'network'],
  VISION: ['deductible', 'copay', 'oopMax', 'network'],
  MEDICARE_ADVANTAGE: ['deductible', 'copay', 'oopMax', 'drugCoverage'],
  MEDICARE_SUPPLEMENT: ['deductible', 'coverageType', 'network'],
  AUTO: ['liabilityLimits', 'collisionDeductible', 'comprehensiveDeductible', 'vehicle'],
  HOME: ['dwellingCoverage', 'deductible', 'liabilityCoverage', 'personalProperty'],
  RENTERS: ['personalProperty', 'deductible', 'liabilityCoverage'],
  FLOOD: ['dwellingCoverage', 'deductible', 'coverageLimit'],
  LIFE: ['coverageAmount', 'productType', 'termLength', 'cashValue',
    'livingBenefits', 'lbCritical', 'lbChronic', 'lbTerminal', 'illustrationLink'],
  ANNUITY: ['coverageAmount', 'productType', 'cashValue'],
  DISABILITY: ['benefitAmount', 'eliminationPeriod', 'benefitPeriod'],
  LONG_TERM_CARE: ['benefitAmount', 'eliminationPeriod', 'benefitPeriod'],
  COMMERCIAL: ['coverageType', 'coverageLimit', 'deductible', 'liabilityCoverage'],
  WORKERS_COMP: ['coverageLimit', 'coverageType', 'deductible'],
  UMBRELLA: ['coverageLimit', 'deductible'],
  PET: ['annualLimit', 'deductible', 'reimbursement'],
  TRAVEL: ['coverageLimit', 'deductible', 'tripLength'],
};
const DEFAULT_FIELDS = ['deductible', 'coverageLimit', 'coverageType'];

// The coverage fields (descriptors) to show for a given plan type. Pass the
// option's coverageDetails too: a plan from the UHC catalog carries a
// `benefitStyle` that picks its own field list. Every screen that shows an
// option has to pass it, or those options lose their "Le paga" rows there.
export function coverageFieldsFor(planType, coverage) {
  const keys = BY_STYLE[coverage?.benefitStyle] || BY_TYPE[planType] || DEFAULT_FIELDS;
  return keys.map((k) => FIELDS[k]).filter(Boolean);
}

// Los campos que SI puede ver el cliente en la pagina publica de la cotizacion.
// Todo lo que lleve `internal` se queda del lado del agente: hoy la referencia
// de la ilustracion, manana lo que se marque igual. Si se filtrara en la
// pantalla en vez de aqui, el siguiente campo interno volveria a publicarse.
export function publicCoverageFieldsFor(planType, coverage) {
  return coverageFieldsFor(planType, coverage).filter((f) => !f.internal);
}

// Friendly label for a stored coverage key (used by the read-only display
// views). Falls back to prettifying the raw key if it isn't in the catalog.
export function coverageLabel(key, es) {
  const f = FIELDS[key];
  if (f) return es ? f.es : f.en;
  return String(key).replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}
